import { useEffect, useRef, useState } from "react";
import { GOOGLE_CHARTS_TYPES } from "../consts/graphs";
import {
  isAbortLikeError,
  runAdminResolvedFiltersPreflight,
  runSharedResolvedFiltersPreflight,
} from "../hooks/resolvedFiltersPreflight";

export type CreateDataHookOptions = {
  /** Call resolve-common-filters once before parallel dashboard requests to warm cache. */
  preflight?: "shared" | "admin";
};

export const formatNumber = (num: number | string) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

type DefaultApiResponse = { data: any[]; total: number };


/**
 * Fetches data in batch from multiple API functions without tracking loading states.
 *
 * @param apiMap - An object where each key maps to a function returning a Promise of DefaultApiResponse.
 * @returns A Promise resolving to an object with the same keys, containing their respective API responses.
 */
export function fetchDashboardDataBatch<
  T extends Record<string, () => Promise<DefaultApiResponse>>
>(
  apiMap: T
): Promise<{ [K in keyof T]: DefaultApiResponse }>;

/**
 * Fetches data in batch from multiple API functions and tracks loading states per key.
 *
 * @param apiMap - An object where each key maps to a function returning a Promise of DefaultApiResponse.
 * @param setIsLoadingMap - A function to update loading state for each API call.
 * @returns A Promise resolving to an object with the same keys, containing their respective API responses.
 */
export function fetchDashboardDataBatch<
  T extends Record<string, () => Promise<DefaultApiResponse>>
>(
  apiMap: T,
  setIsLoadingMap: (
    cb: (prev: Partial<Record<keyof T, boolean>>) => Partial<Record<keyof T, boolean>>
  ) => void
): Promise<{ [K in keyof T]: DefaultApiResponse }>;

/**
 * Fetches data in batch from multiple API functions and tracks loading states per key.
 *
 * @param apiMap - An object where each key maps to a function returning a Promise of DefaultApiResponse.
 * @param setIsLoadingMap - A function to update loading state for each API call.
 * @param setData - A function to update data for each API call.
 * @returns A Promise resolving to an object with the same keys, containing their respective API responses.
 */
export function fetchDashboardDataBatch<T extends Record<string, () => Promise<DefaultApiResponse>>>(
  apiMap: T,
  setIsLoadingMap: (
    cb: (prev: Partial<Record<keyof T, boolean>>) => Partial<Record<keyof T, boolean>>
  ) => void,
  setData: (cb: (prev: Partial<Record<keyof T, DefaultApiResponse>>) => Partial<Record<keyof T, DefaultApiResponse>>) => void
): Promise<{ [K in keyof T]: DefaultApiResponse }>;

/**
 * Implementation of fetchDashboardDataBatch, supporting optional loading state tracking.
 *
 * @param apiMap - An object where each key maps to a function returning a Promise of DefaultApiResponse.
 * @param setIsLoadingMap - A function to update loading state for each API call.
 * @param setData - A function to update data for each API call.
 * @returns A Promise resolving to an object with the same keys, containing their respective API responses.
 */
export async function fetchDashboardDataBatch<
  T extends Record<string, () => Promise<DefaultApiResponse>>
>(
  apiMap: T,
  setIsLoadingMap?: (
    cb: (prev: Partial<Record<keyof T, boolean>>) => Partial<Record<keyof T, boolean>>
  ) => void,
  setData?: (
    cb: (prev: Partial<Record<keyof T, DefaultApiResponse>>) => Partial<Record<keyof T, DefaultApiResponse>>
  ) => void
): Promise<{ [K in keyof T]: DefaultApiResponse }> {
  const results = await Promise.allSettled(
    Object.entries(apiMap).map(async ([key, apiFn]) => {
      try {
        if (setIsLoadingMap) {
          setIsLoadingMap(prev => ({ ...prev, [key]: true }));
        }

        const res = await apiFn();

        if (setData) {
          setData(prev => ({ ...prev, [key]: res }));
        }

        return { key, res };
      } catch (e) {
        const fallback = { data: [], total: 0 };

        if (setData) {
          setData(prev => ({ ...prev, [key]: fallback }));
        }

        return { key, res: fallback };
      } finally {
        if (setIsLoadingMap) {
          setIsLoadingMap(prev => ({ ...prev, [key]: false }));
        }
      }
    })
  );

  return results.reduce((acc, result) => {
    if (result.status === "fulfilled") {
      const { key, res } = result.value;
      acc[key as keyof T] = res;
    }
    return acc;
  }, {} as { [K in keyof T]: DefaultApiResponse });
}


type FetchDashboardDataParams = {
  filters: Record<string, any>;
  graphs?: any[];
};

type ParamsWithDefaults = {
  chartType: string;
  limit: number;
  page: number;
  [key: string]: any;
};

/**
 * Extracts the full API path (starting from /api) from apiService
 * Looks for basePath property or tries to extract from function source
 */
function extractFullPathFromApiService<T extends Record<string, any>>(
  apiService: T,
  endpoint: string
): string | null {
  // First check if apiService has a basePath property
  if ((apiService as any)?.basePath) {
    const basePath = (apiService as any).basePath;
    // Ensure it starts with /api
    const normalizedBase = basePath.startsWith('/api') ? basePath : `/api${basePath.startsWith('/') ? '' : '/'}${basePath}`;
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${normalizedBase}${normalizedEndpoint}`;
  }
  
  // Try to extract base path from function source by looking for createAxiosInstance pattern
  // This is a heuristic - the basePath should ideally be provided as a property
  try {
    const firstKey = Object.keys(apiService).find(key => typeof apiService[key as keyof T] === 'function');
    if (firstKey) {
      const fn = apiService[firstKey as keyof T] as Function;
      const fnString = fn.toString();
      
      // Look for createAxiosInstance pattern with /api/ path
      // Pattern: createAxiosInstance(`.../api/...`) or createAxiosInstance(".../api/...")
      const basePathMatch = fnString.match(/createAxiosInstance\s*\(\s*[`"'][^`"']*(\/api\/[a-z0-9-]+(?:\/[a-z0-9-]+)*)[^`"']*[`"']/i);
      if (basePathMatch && basePathMatch[1]) {
        const basePath = basePathMatch[1];
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        return `${basePath}${normalizedEndpoint}`;
      }
      
      // Alternative: look for /api/ pattern anywhere in the function
      const apiPattern = fnString.match(/(\/api\/[a-z0-9-]+(?:\/[a-z0-9-]+)*)/i);
      if (apiPattern && apiPattern[1]) {
        const basePath = apiPattern[1];
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        return `${basePath}${normalizedEndpoint}`;
      }
    }
  } catch (error) {
    // Silently fail
  }
  
  // If endpoint already starts with /api, return it as-is
  if (endpoint.startsWith('/api')) {
    return endpoint;
  }
  
  return null;
}

/**
 * Extracts API endpoint from a function's source code by looking for request.get/post/put/delete patterns
 * Handles both development and production (minified) code
 */
function extractEndpointFromFunction(fn: Function): string | null {
  try {
    const fnString = fn.toString();
    
    // Match patterns like: request.get("/endpoint", ...) or request.post("/endpoint", ...)
    // Pattern: request.METHOD("endpoint" or 'endpoint' or `endpoint`)
    // Also handle minified code where spaces might be removed: request.get("/endpoint",...)
    const match = fnString.match(/request\.(get|post|put|delete|patch)\s*\(\s*["'`]([^"'`]+)["'`]/);
    if (match && match[2]) {
      return match[2];
    }
    
    // Try to match template literals with variables (e.g., `/endpoint/${id}`)
    const templateMatch = fnString.match(/request\.(get|post|put|delete|patch)\s*\(\s*`([^`]+)`/);
    if (templateMatch && templateMatch[2]) {
      // Extract the base path before any ${variable}
      const baseEndpointPath = templateMatch[2].split('${')[0];
      // Remove trailing slash if it's just a variable placeholder
      return baseEndpointPath.endsWith('/') && baseEndpointPath.length > 1 
        ? baseEndpointPath.slice(0, -1) 
        : (baseEndpointPath || templateMatch[2]);
    }
    
    // Fallback: try to find any string that looks like an endpoint path
    // This handles minified code better
    const endpointPattern = /["'`](\/[a-z0-9-]+(?:\/[a-z0-9-]+)*)["'`]/i;
    const fallbackMatch = fnString.match(endpointPattern);
    if (fallbackMatch && fallbackMatch[1] && !fallbackMatch[1].startsWith('/api')) {
      // Only return if it looks like an endpoint (starts with / and doesn't start with /api)
      return fallbackMatch[1];
    }
  } catch (error) {
    // Silently fail if extraction is not possible
  }
  
  return null;
}

/**
 * Dynamically extracts full API paths (starting from /api) from an API service object
 * Prioritizes basePath property for reliability in production
 */
function extractEndpointsFromApiService<T extends Record<string, any>>(
  apiService: T
): Partial<Record<keyof T, string>> {
  const endpoints: Partial<Record<keyof T, string>> = {};
  
  // First check if apiService has an endpoints property
  if ((apiService as any)?.endpoints) {
    return (apiService as any).endpoints;
  }
  
  // Get basePath if available - this is the most reliable method
  const basePath = (apiService as any)?.basePath;
  
  // Extract full paths from each function
  Object.keys(apiService).forEach((key) => {
    // Skip basePath and endpoints properties
    if (key === 'basePath' || key === 'endpoints') {
      return;
    }
    
    const fn = apiService[key as keyof T];
    if (typeof fn === 'function') {
      const endpoint = extractEndpointFromFunction(fn);
      if (endpoint) {
        // If we have basePath, use it directly (most reliable)
        if (basePath) {
          const normalizedBase = basePath.startsWith('/api') ? basePath : `/api${basePath.startsWith('/') ? '' : '/'}${basePath}`;
          const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
          endpoints[key as keyof T] = `${normalizedBase}${normalizedEndpoint}`;
        } else {
          // Try to extract full path starting from /api
          const fullPath = extractFullPathFromApiService(apiService, endpoint);
          if (fullPath) {
            endpoints[key as keyof T] = fullPath;
          } else {
            // Fallback: if endpoint already starts with /api, use it as-is
            if (endpoint.startsWith('/api')) {
              endpoints[key as keyof T] = endpoint;
            }
          }
        }
      }
    }
  });
  
  return endpoints;
}

/**
 * Creates a data hook for fetching dashboard data.
 *
 * @param apiService - The API service object containing the API functions.
 * @param generateApiMap - A function that generates an API map based on filters, parameters, and an abort controller.
 * @param hookOptions - Optional preflight to warm resolved-filters cache before the parallel batch.
 * @returns A function that returns an object containing the data, loading state, loading state map, and endpoints map.
 */
export function createDataHook<T extends Record<string, any>>(
  apiService: T,
  generateApiMap: (
    filters: Record<string, any>,
    params: ParamsWithDefaults,
    controller: AbortController,
    apiServiceToUse?: T,
    graphs?: any[]
  ) => { [K in keyof T]?: () => Promise<any> },
  hookOptions?: CreateDataHookOptions
) {
  // Dynamically extract endpoints from apiService, with fallback to provided endpoints
  // If endpoints are explicitly provided, use them; otherwise extract from apiService
  const extractedEndpoints = extractEndpointsFromApiService(apiService);
  
  return function useDashboardData({ filters, graphs = [] }: FetchDashboardDataParams) {
    const [data, setData] = useState<Partial<Record<keyof T, any>>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMap, setIsLoadingMap] = useState<Partial<Record<keyof T, boolean>>>({});
    // Initialize endpointsMap with extracted endpoints to ensure it works in production
    const [endpointsMap, setEndpointsMap] = useState<Partial<Record<keyof T, string>>>(extractedEndpoints);
    const abortControllerRef = useRef<AbortController | null>(null);
    //const isFirstRender = useRef(true);
    const pendingRequestsRef = useRef<Partial<Record<keyof T, boolean>>>({});
    // Store original parameters for each API call to preserve them during pagination
    const originalParamsRef = useRef<Partial<Record<keyof T, ParamsWithDefaults>>>({});

    // Helper to get version for a chartId
    const getVersionForChart = (chartId: string): number => {
      const graph = graphs.find((g: any) => g.chartId === chartId);
      return graph?.selectedVersion || 1;
    };

    const params: ParamsWithDefaults = {
      ...filters,
      chartType: GOOGLE_CHARTS_TYPES.TABLE,
      limit: 15,
      page: 1,
    };

    // Create a proxy around the API service to capture parameters and add version
    // Also dynamically extract and store endpoints when functions are accessed
    const proxiedApiService = new Proxy(apiService, {
      get(target, prop) {
        const originalMethod = target[prop as keyof T];
        if (typeof originalMethod === 'function') {
          // Dynamically extract full path if not already in endpointsMap
          // Use functional update to avoid stale closure issues
          setEndpointsMap(prev => {
            if (prev[prop as keyof T]) {
              return prev; // Already has endpoint, no need to update
            }
            
            const endpoint = extractEndpointFromFunction(originalMethod);
            if (endpoint) {
              // Get basePath if available (most reliable method)
              const basePath = (apiService as any)?.basePath;
              let fullPath: string | null = null;
              
              if (basePath) {
                const normalizedBase = basePath.startsWith('/api') ? basePath : `/api${basePath.startsWith('/') ? '' : '/'}${basePath}`;
                const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
                fullPath = `${normalizedBase}${normalizedEndpoint}`;
              } else {
                // Try to extract full path starting from /api
                fullPath = extractFullPathFromApiService(apiService, endpoint);
                if (!fullPath && endpoint.startsWith('/api')) {
                  fullPath = endpoint;
                }
              }
              
              if (fullPath) {
                return {
                  ...prev,
                  [prop]: fullPath
                };
              }
            }
            
            return prev;
          });
          
          return function(...args: any[]) {
            // Get chartId from function name (prop) and add version parameter
            const chartId = String(prop);
            const version = getVersionForChart(chartId);
            
            // Capture the parameters passed to this API call and add version
            if (args.length > 0 && typeof args[0] === 'object') {
              const paramsWithVersion = { ...args[0], v: version };
              originalParamsRef.current[prop as keyof T] = paramsWithVersion;
              return originalMethod.apply(target, [paramsWithVersion, args[1]]);
            }
            return originalMethod.apply(target, args);
          };
        }
        return originalMethod;
      }
    });

    useEffect(() => {
     /* if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }*/

      const fetchData = async () => {
        setIsLoading(true);
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          const preflightMap: Record<string, (filters: any, signal: AbortSignal) => Promise<void>> = {
            shared: runSharedResolvedFiltersPreflight,
            admin: runAdminResolvedFiltersPreflight,
          };
          
          if (hookOptions?.preflight && preflightMap[hookOptions.preflight]) {
            try {
              await preflightMap[hookOptions.preflight](filters, controller.signal);
            } catch (e) {
              if (isAbortLikeError(e)) throw e;
              console.warn(
                `${hookOptions.preflight.charAt(0).toUpperCase() + hookOptions.preflight.slice(1)} resolved-common-filters preflight failed; continuing with dashboard requests.`,
                e
              );
            }
          }

          const apiMap = generateApiMap(filters, params, controller, proxiedApiService, graphs);
          
          // Extract endpoints from the API map keys to ensure they're populated
          // This is a fallback in case the Proxy-based extraction didn't work
          const apiMapKeys = Object.keys(apiMap);
          
          // Use functional update to avoid stale closure issues
          setEndpointsMap(prev => {
            const endpointsToAdd: Partial<Record<keyof T, string>> = {};
            const basePath = (apiService as any)?.basePath;
            
            apiMapKeys.forEach((key) => {
              // Only add if not already in endpointsMap
              if (!prev[key as keyof T]) {
                const fn = apiService[key as keyof T];
                if (typeof fn === 'function') {
                  const endpoint = extractEndpointFromFunction(fn);
                  if (endpoint) {
                    let fullPath: string | null = null;
                    
                    // Use basePath if available (most reliable method)
                    if (basePath) {
                      const normalizedBase = basePath.startsWith('/api') ? basePath : `/api${basePath.startsWith('/') ? '' : '/'}${basePath}`;
                      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
                      fullPath = `${normalizedBase}${normalizedEndpoint}`;
                    } else {
                      // Try to extract full path starting from /api
                      fullPath = extractFullPathFromApiService(apiService, endpoint);
                      if (!fullPath && endpoint.startsWith('/api')) {
                        fullPath = endpoint;
                      }
                    }
                    
                    if (fullPath) {
                      endpointsToAdd[key as keyof T] = fullPath;
                    }
                  }
                }
              }
            });
            
            // Only update if we found new endpoints
            if (Object.keys(endpointsToAdd).length > 0) {
              return { ...prev, ...endpointsToAdd };
            }
            
            return prev;
          });
          
          await fetchDashboardDataBatch(apiMap as any, setIsLoadingMap, setData);
        } catch (error: any) {
          if (!isAbortLikeError(error)) {
            console.error("Fetch failed:", error);
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [JSON.stringify(filters)]);

    const fetchDataForKey = async (
      key: keyof T,
      event?: any,
      scrollDirection?: "up" | "down",
      chartType?: string
    ) => {
      if (pendingRequestsRef.current[key]) return;
      if (scrollDirection === "up" && chartType === GOOGLE_CHARTS_TYPES.TABLE) return;

      const currentData = data[key];
      if (currentData && currentData.data) {
        const page = currentData.page || 1;
        let dataOnKey = currentData.data;
        const totalRecords = currentData.total || 0;
        const originalParams = originalParamsRef.current[key] || params;
        const limit = originalParams.limit || params.limit;
        const totalPages = Math.ceil(totalRecords / limit);
        const nextPage = page + (scrollDirection === "down" ? 1 : -1);

        if (nextPage < 1 || nextPage > totalPages) return;

        pendingRequestsRef.current[key] = true;
        
        setIsLoadingMap(prev => ({ ...prev, [key]: true }));

        try {
          const originalParams = originalParamsRef.current[key] || params;
          
          /*if (chartType === GOOGLE_CHARTS_TYPES.TABLE) {
            // For TABLE chart type, fetch all data in one API call without pagination
            const response = await proxiedApiService[key]({
              ...originalParams,
              page: undefined,
              limit: undefined,
            });
            
            setData(prev => ({
              ...prev,
              [key]: { 
                ...response, 
                page: totalPages // Mark as fully loaded
              },
            }));
          }*/
          if (chartType === GOOGLE_CHARTS_TYPES.TABLE) {
            // For TABLE chart type, fetch all data in one API call without pagination
            const response = await proxiedApiService[key]({
              ...originalParams,
              page: nextPage,
            });
            
            setData(prev => {
              // Remove the first data row before pushing new data
              const prevData = prev[key]?.data ? prev[key].data : [];
              const newData = response.data ? response.data.slice(1) : [];
              
              return {
                ...prev,
                [key]: {
                  ...response,
                  data: [...prevData, ...newData],
                  page: nextPage,
                },
              };
            });
          } else {
            const response = await proxiedApiService[key]({
              ...originalParams,
              page: nextPage,
            });

            dataOnKey = response.data as string[];

            setData(prev => ({
              ...prev,
              [key]: { ...response, data: dataOnKey },
            }));
          }
        } catch (error) {
          console.error(`Error fetching data for ${String(key)}:`, error);
        } finally {
          pendingRequestsRef.current[key] = false;
          setIsLoadingMap(prev => ({ ...prev, [key]: false }));
        }
      }
    };

    const fetchDataForOtherAction = async (
      key: keyof T,
      otherActionParams: Record<string, any>
    ) => {
      if (pendingRequestsRef.current[key]) return;

      pendingRequestsRef.current[key] = true;
      setIsLoadingMap(prev => ({ ...prev, [key]: true }));

      try {
        const originalParams = originalParamsRef.current[key] || params;
        
        const response = await proxiedApiService[key]({
          ...originalParams,
          ...otherActionParams,
          page: 1, // Reset to first page when other action is triggered
        });
        
        // Update the original params to reflect the new page state
        originalParamsRef.current[key] = {
          ...originalParams,
          ...otherActionParams,
          page: 1,
        };
        
        setData(prev => ({
          ...prev,
          [key]: response,
        }));
      } catch (error) {
        console.error(`Error fetching data for ${String(key)} with other action:`, error);
      } finally {
        pendingRequestsRef.current[key] = false;
        setIsLoadingMap(prev => ({ ...prev, [key]: false }));
      }
    };

    const fetchDataForSorting = async (
      key: keyof T,
      sortBy: string,
      sortOrder: "asc" | "desc"
    ) => {
      if (pendingRequestsRef.current[key]) return;

      pendingRequestsRef.current[key] = true;
      setIsLoadingMap(prev => ({ ...prev, [key]: true }));

      try {
        const originalParams = originalParamsRef.current[key] || params;
        
        const response = await proxiedApiService[key]({
          ...originalParams,
          sortBy,
          sortOrder,
          page: 1, // Reset to first page when sorting
        });
        
        setData(prev => ({
          ...prev,
          [key]: response,
        }));
      } catch (error) {
        console.error(`Error fetching data for ${String(key)} with sorting:`, error);
      } finally {
        pendingRequestsRef.current[key] = false;
        setIsLoadingMap(prev => ({ ...prev, [key]: false }));
      }
    };

    const fetchDataForPagination = async (
      key: keyof T,
      targetPage: number
    ) => {
      if (pendingRequestsRef.current[key]) return;

      const currentData = data[key];
      if (!currentData) return;

      const originalParams = originalParamsRef.current[key] || params;
      const limit = originalParams.limit || params.limit;
      const totalRecords = currentData.total || 0;
      const totalPages = Math.ceil(totalRecords / limit);

      if (targetPage < 1 || targetPage > totalPages) return;

      pendingRequestsRef.current[key] = true;
      setIsLoadingMap(prev => ({ ...prev, [key]: true }));

      try {
        const response = await proxiedApiService[key]({
          ...originalParams,
          page: targetPage,
        });
        
        setData(prev => ({
          ...prev,
          [key]: response,
        }));
      } catch (error) {
        console.error(`Error fetching data for ${String(key)} with pagination:`, error);
      } finally {
        pendingRequestsRef.current[key] = false;
        setIsLoadingMap(prev => ({ ...prev, [key]: false }));
      }
    };

    const getPaginationInfo = (key: keyof T) => {
      const currentData = data[key];
      if (!currentData) return null;

      const originalParams = originalParamsRef.current[key] || params;
      const limit = originalParams.limit || params.limit;
      const currentPage = currentData.page || 1;
      const totalRecords = currentData.total || 0;
      const totalPages = Math.ceil(totalRecords / limit);

      return {
        currentPage,
        totalPages,
        totalRecords,
        limit,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
        startRecord: (currentPage - 1) * limit + 1,
        endRecord: Math.min(currentPage * limit, totalRecords),
      };
    };

    const handlePaginationChange = async (
      key: keyof T,
      action: 'next' | 'previous' | 'page',
      pageNumber?: number
    ) => {
      const currentData = data[key];
      if (!currentData) return;

      const originalParams = originalParamsRef.current[key] || params;
      const limit = originalParams.limit || params.limit;
      const currentPage = currentData.page || 1;
      const totalRecords = currentData.total || 0;
      const totalPages = Math.ceil(totalRecords / limit);

      let targetPage: number;

      switch (action) {
        case 'next':
          targetPage = currentPage + 1;
          if (targetPage > totalPages) return;
          break;
        case 'previous':
          targetPage = currentPage - 1;
          if (targetPage < 1) return;
          break;
        case 'page':
          if (pageNumber === undefined || pageNumber < 1 || pageNumber > totalPages) return;
          targetPage = pageNumber;
          break;
        default:
          return;
      }

      await fetchDataForPagination(key, targetPage);
    };

    return {
      isLoading,
      data,
      isLoadingMap,
      endpointsMap,
      otherActions: fetchDataForOtherAction,
      onScroll: fetchDataForKey,
      onSortChange: (chartId: string, columnId: string, desc: boolean) => 
        fetchDataForSorting(chartId as keyof T, columnId, desc ? 'desc' : 'asc'),
      paginationActions: {
        getPaginationInfo: (chartId: string) => getPaginationInfo(chartId as keyof T),
        handlePaginationChange: (chartId: string, action: 'next' | 'previous' | 'page', pageNumber?: number) => 
          handlePaginationChange(chartId as keyof T, action, pageNumber)
      },
      exportActions: {
        fetchAllDataForExport: async (chartId: string, sortBy?: string, sortOrder?: 'asc' | 'desc', signal?: AbortSignal) => {
          if (pendingRequestsRef.current[chartId as keyof T]) return null;
          
          pendingRequestsRef.current[chartId as keyof T] = true;
          setIsLoadingMap(prev => ({ ...prev, [chartId]: true }));
          
          try {
            const originalParams = originalParamsRef.current[chartId as keyof T] || params;
            
            const response = await proxiedApiService[chartId as keyof T]({
              ...originalParams,
              page: undefined,
              limit: undefined,
              sortBy,
              sortOrder,
            }, signal);
            
            return response?.data || [];
          } catch (error: any) {
            if (error?.name === 'AbortError') {
              console.log(`Export request for ${String(chartId)} was cancelled`);
              return null;
            }
            
            console.error(`Error fetching all data for export ${String(chartId)}:`, error);
            return null;
          } finally {
            pendingRequestsRef.current[chartId as keyof T] = false;
            setIsLoadingMap(prev => ({ ...prev, [chartId]: false }));
          }
        }
      },
    };
  };
}